import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { ProtocolLogo } from "@/components/sector/ProtocolLogo";
import { EntityType, entityTrack, useEntityNav } from "@/lib/entities";

/* Canonical Entity identity — THE entity-navigation primitive.
   Live entity → one accessible clickable target with an obvious affordance.
   Non-live entity → identical identity rendered INERT (Founder Rule 6):
   no link, no pointer, no chevron, no search fallback. Routing activates
   automatically platform-wide when the registry flips `live`. */

export const EntityLogo = ({ type, slug, name, size = 22 }: {
  type: EntityType; slug: string; name: string; size?: number;
}) => {
  const [err, setErr] = useState(false);
  if (type === "protocol") return <ProtocolLogo slug={slug} name={name} size={size} />;
  const initials = (name || slug).split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const hasAsset = (type === "chain" || type === "token") && !err;
  return (
    <span aria-hidden data-testid={`entity-logo-${type}-${slug}`}
      className="grid place-items-center rounded-full shrink-0 border border-white/[0.14] bg-white/[0.05] font-mono font-bold text-gray-200"
      style={{ width: size, height: size, fontSize: Math.max(8, size * 0.34) }}>
      {hasAsset ? (
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/v1/assets/${type}/${encodeURIComponent(slug)}`}
          alt="" width={size - 4} height={size - 4} loading="lazy" decoding="async"
          onError={() => setErr(true)} className="rounded-full"
          style={{ width: size - 4, height: size - 4 }} />
      ) : initials}
    </span>
  );
};

export const EntityLink = ({ type, slug, name, size = 22, className = "", surface = "entity_link" }: {
  type: EntityType; slug: string; name?: string; size?: number;
  className?: string; surface?: string;
}) => {
  const { entity, live, route } = useEntityNav(type, slug);
  const display = name || entity?.name || slug;
  if (!live || !route) {
    return (
      <span data-testid={`entity-inert-${type}-${slug}`}
        className={`inline-flex items-center gap-2 min-w-0 ${className}`}>
        <EntityLogo type={type} slug={slug} name={display} size={size} />
        <span className="font-semibold text-white truncate">{display}</span>
      </span>
    );
  }
  return (
    <Link to={route}
      aria-label={`${display} — Entity Intelligence`}
      data-testid={`entity-link-${type}-${slug}`}
      onClick={() => entityTrack("entity_nav_click", `${type}:${slug}`, { surface })}
      className={`group inline-flex items-center gap-2 min-w-0 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60 ${className}`}>
      <EntityLogo type={type} slug={slug} name={display} size={size} />
      <span className="font-semibold text-white truncate transition-colors duration-200 group-hover:text-[#00E5FF]">{display}</span>
      <ArrowUpRight aria-hidden
        className="h-3 w-3 shrink-0 text-[#00E5FF] opacity-60 md:opacity-0 transition-opacity duration-200 md:group-hover:opacity-100 md:group-focus-visible:opacity-100" />
    </Link>
  );
};
